import { useMemo } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import AllInclusiveRoundedIcon from '@mui/icons-material/AllInclusiveRounded'
import { useQuery } from '@tanstack/react-query'

import { api } from '../lib/api'
import { useCustomerStore } from '../store/customerStore'
import { SearchableSelect } from './SearchableSelect'

interface Props {
  /// Currently-selected customer id. `null` means "All customers"
  /// (shared/org-wide) for catalog mode, or "no selection yet" for
  /// required mode.
  value: string | null
  onChange: (id: string | null) => void
  /// `required` — sites and other strict resources: must pick a
  /// specific customer; no "All customers" option.
  /// `catalog` — vendors / checklists / workflows / SLA / categories /
  /// parts: "All customers" is the default and means org-wide.
  mode: 'required' | 'catalog'
  /// Label shown above the picker. Defaults match the standard
  /// "Customer" wording but the caller can override for context
  /// (e.g. "Customer scope" on catalog forms).
  label?: string
  helperText?: string
  disabled?: boolean
  size?: 'small' | 'medium'
}

// The option shape SearchableSelect renders — a real customer, or (in
// catalog mode) the synthetic "All customers" row standing in for a
// null customer_id.
interface PickerOption {
  id: string
  name: string
  site_count?: number
  isAll?: boolean
}

const ALL_CUSTOMERS_OPTION: PickerOption = { id: '', name: 'All customers', isAll: true }

/// Reusable customer-scope picker.
///
/// Two modes:
///   • required — site create, asset create (when no site), etc.
///                Renders the customer list; no "All customers".
///   • catalog  — vendors, checklist templates, workflows, SLA
///                policies, asset categories, parts. Renders an
///                "All customers" option at the top representing
///                NULL customer_id (shared across the whole org).
///
/// Built on SearchableSelect (Autocomplete) rather than a plain MUI
/// Select so orgs with a long customer list can type to filter instead
/// of scrolling a dropdown.
///
/// Auto-hides itself for single-customer accounts in catalog mode —
/// when there's only one customer in the org there's no choice to
/// make. In required mode we still render it (with the one option
/// pre-selected) so the saved value is explicit on the row.
export function CustomerScopePicker({
  value, onChange, mode, label, helperText, disabled, size = 'small',
}: Props) {
  const customersQ = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.customers.list(),
    staleTime: 5 * 60_000,
  })
  const allCustomers = Array.isArray(customersQ.data) ? customersQ.data : []

  // Narrow the pickable list to whatever's currently active in the
  // header switcher — attaching a catalog row to "any customer in the
  // org" when you're working inside one customer's (or one group's)
  // view is almost never what's intended. Consolidated "All customers"
  // mode (nothing active) keeps offering the full list, same as before.
  const activeCustomerId = useCustomerStore((s) => s.customerId)
  const groupScope = useCustomerStore((s) => s.groupScope)
  const groupTreeQ = useQuery({
    queryKey: ['customer-groups-tree'],
    queryFn: () => api.customerGroups.tree(),
    staleTime: 5 * 60_000,
    enabled: !!groupScope,
  })

  const customers = useMemo(() => {
    let scoped = allCustomers
    if (activeCustomerId) {
      scoped = allCustomers.filter((c) => c.id === activeCustomerId)
    } else if (groupScope) {
      const tree = groupTreeQ.data ?? []
      const subgroupIds = groupScope.kind === 'subgroup'
        ? new Set([groupScope.id])
        : new Set((tree.find((g) => g.id === groupScope.id)?.children ?? []).map((g) => g.id))
      scoped = allCustomers.filter((c) => c.group_id && subgroupIds.has(c.group_id))
    }
    // Always keep the currently-selected value pickable even if it
    // falls outside the active scope (e.g. editing a row attached
    // elsewhere) — otherwise the field would silently show blank.
    if (value && !scoped.some((c) => c.id === value)) {
      const existing = allCustomers.find((c) => c.id === value)
      if (existing) return [...scoped, existing]
    }
    return scoped
  }, [allCustomers, activeCustomerId, groupScope, groupTreeQ.data, value])

  // Hide entirely in catalog mode for single-customer orgs — leaving
  // the field NULL is implicit and matches the only customer. Based on
  // the org's full customer count, not the (possibly narrower) active
  // scope — "All customers" vs. "this one" is still a real choice.
  if (mode === 'catalog' && customersQ.isSuccess && allCustomers.length <= 1) {
    return null
  }
  if (customersQ.isLoading) return null

  const displayLabel = label ?? (mode === 'required' ? 'Customer' : 'Customer scope')
  const options: PickerOption[] = mode === 'catalog'
    ? [ALL_CUSTOMERS_OPTION, ...customers]
    : customers

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
          {displayLabel}{mode === 'required' && <span style={{ color: '#d32f2f', marginLeft: 4 }}>*</span>}
        </Typography>
      </Stack>
      <SearchableSelect
        value={value ?? ''}
        onChange={(v) => onChange(v === '' ? null : v)}
        options={options}
        getValue={(o) => o.id}
        getLabel={(o) => o.isAll ? 'All customers (shared)' : o.name}
        placeholder="Search customers…"
        disabled={disabled}
        size={size}
        renderOption={(o) => (
          <Stack direction="row" alignItems="center" spacing={1}>
            {o.isAll
              ? <AllInclusiveRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              : <BusinessOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
            <Box>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{o.isAll ? 'All customers' : o.name}</Typography>
              {o.isAll ? (
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                  Shared org-wide — available to every customer
                </Typography>
              ) : typeof o.site_count === 'number' ? (
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                  {o.site_count} site{o.site_count === 1 ? '' : 's'}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        )}
      />
      {helperText && (
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.5 }}>
          {helperText}
        </Typography>
      )}
    </Box>
  )
}
